import { useEffect, useState } from 'react';
import { MapPin, Maximize2 } from 'lucide-react';

const AnimatedTour = ({ cities = [], countryName }) => {
  const [currentCityIndex, setCurrentCityIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  // Fallback image URL
  const fallbackImage = 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80';

  const handleImageError = (cityName) => {
    setImageErrors(prev => ({ ...prev, [cityName]: true }));
  };

  useEffect(() => {
    if (cities.length === 0) return;

    const interval = setInterval(() => {
      setCurrentCityIndex((prev) => (prev + 1) % cities.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [cities]);

  if (!cities || cities.length === 0) return null;

  const currentCity = cities[currentCityIndex];

  return (
    <div className={`relative w-full rounded-2xl overflow-hidden border border-[#D4AF37]/20 shadow-2xl transition-all duration-500 ${isFullscreen ? 'fixed inset-4 z-50 h-[calc(100vh-2rem)]' : 'h-[500px]'}`}>
      {/* Fullscreen Toggle */}
      <button
        onClick={() => setIsFullscreen(!isFullscreen)}
        className="absolute top-4 left-4 z-20 p-3 bg-[#D4AF37]/90 text-black rounded-full hover:bg-[#D4AF37] transition-all shadow-lg backdrop-blur-sm"
        title={isFullscreen ? 'خروج از تمام صفحه' : 'تمام صفحه'}
      >
        <Maximize2 size={20} />
      </button>

      {/* Background Image with Parallax Effect */}
      {cities.map((city, index) => {
        const imageSrc = imageErrors[city.name] || !city.image ? fallbackImage : city.image;
        return (
          <div
            key={city.name}
            className={`absolute inset-0 transition-all duration-1000 ${
              index === currentCityIndex
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-110'
            }`}
            style={{
              backgroundImage: `url(${imageSrc})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
            {/* Hidden img tag for error detection */}
            {city.image && !imageErrors[city.name] && (
              <img
                src={city.image}
                alt=""
                style={{ display: 'none' }}
                onError={() => handleImageError(city.name)}
              />
            )}
          </div>
        );
      })}

      {/* Content Overlay */}
      <div className="relative z-10 h-full flex flex-col justify-between p-8">
        {/* Header */}
        <div className="text-center">
          <div className="inline-block glass-effect border border-[#D4AF37]/30 rounded-full px-6 py-3">
            <h3 className="text-xl font-light text-[#D4AF37] flex items-center gap-2">
              <MapPin size={20} className="animate-pulse" />
              تور مجازی {countryName}
            </h3>
          </div>
        </div>

        {/* City Name Display */}
        <div className="text-center">
          {cities.map((city, index) => (
            <div
              key={city.name}
              className={`transition-all duration-1000 ${
                index === currentCityIndex
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 absolute translate-y-8'
              }`}
            >
              <div className="glass-effect border border-[#D4AF37]/40 rounded-2xl px-12 py-8 inline-block backdrop-blur-xl">
                <div className="flex items-center gap-4 mb-3">
                  <div className="relative">
                    <MapPin className="text-[#D4AF37]" size={40} />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#D4AF37] rounded-full animate-ping"></div>
                  </div>
                  <h2 className="text-5xl font-light text-white">{city.name}</h2>
                </div>
                <p className="text-sm text-zinc-300 font-light">
                  {index === 0 ? 'پایتخت' : 'یکی از شهرهای برتر برای زندگی و تحصیل'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Progress Indicators */}
        <div className="flex justify-center gap-3">
          {cities.map((city, index) => (
            <div
              key={city.name}
              className="relative"
            >
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  index === currentCityIndex
                    ? 'w-12 bg-[#D4AF37]'
                    : 'w-2 bg-white/30 hover:bg-white/50'
                }`}
              ></div>
              {index === currentCityIndex && (
                <div className="absolute inset-0 bg-[#D4AF37] rounded-full animate-ping opacity-75"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Decorative Animated Elements */}
      <div className="absolute top-8 right-8 text-6xl opacity-20 animate-float">
        ✈️
      </div>
      <div className="absolute bottom-8 left-8 text-5xl opacity-20 animate-float-delayed">
        🏙️
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
};

export default AnimatedTour;
